import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { accountApi } from '../api/client';

// Outcome cache keyed by token, persisted via `sessionStorage` so it
// survives every flavour of remount we have seen in dev — React 18
// StrictMode unmount/remount, CRA HMR module reloads, and the brief
// component re-render that follows an in-flight `setState`. Each
// token is confirmed at most once per browser tab session; the
// backend remains the strict source of truth for token reuse.
const STORAGE_PREFIX = 'herborist:emailchange:';
const _readOutcome = (token) => {
  try {
    const v = sessionStorage.getItem(STORAGE_PREFIX + token);
    return v ? JSON.parse(v) : null;
  } catch (_e) { return null; }
};
const _writeOutcome = (token, outcome) => {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + token, JSON.stringify(outcome));
  } catch (_e) { /* storage disabled — fall back to single-shot fetch */ }
};

/**
 * Confirmation page reached from the email-change confirmation link.
 *
 * The page POSTs the token to the confirm endpoint immediately on
 * mount so the user does not have to click a second button.
 * Successful confirmation forces a redirect to /login (the auth
 * cookie still references the OLD email until the next login, so
 * we send the user there explicitly).
 *
 * Double-submit defence (Feb-2026 fix):
 * React 18's StrictMode runs effects twice in dev. Without the
 * guard below, the first call would succeed (token marked used by
 * the backend) and the second call would 400 with
 * "Ongeldige of reeds gebruikte bevestigingslink", overwriting our
 * success state. We therefore:
 *   - use a `useRef` to ensure the confirm POST fires at most once
 *     per mounted token, regardless of how many times the effect
 *     re-runs;
 *   - additionally, the error branch refuses to overwrite a status
 *     that is already `ok` — belt-and-braces against any future
 *     re-entry path (router transitions, hot reload, etc.).
 * Backend token-reuse semantics remain strict and unchanged.
 */
export default function ConfirmEmailChange() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get('token') || '', [params]);

  const [status, setStatus] = useState(token ? 'pending' : 'no-token');
  const [error, setError] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const applyOutcome = (o) => {
      if (cancelled) return;
      if (o.state === 'ok') {
        setNewEmail(o.newEmail || '');
        setStatus('ok');
        setTimeout(() => navigate('/login'), 2500);
      } else if (o.state === 'error') {
        if (statusRef.current === 'ok') return;
        setError(o.detail || 'De bevestigingslink is ongeldig of verlopen.');
        setStatus('error');
      }
    };

    const cached = _readOutcome(token);
    if (cached && cached.state !== 'pending') {
      // A previous mount already finished — apply that outcome directly
      // and never hit the network again.
      applyOutcome(cached);
      return () => { cancelled = true; };
    }
    if (cached && cached.state === 'pending') {
      // Another mount is in flight. Poll the cache rather than firing
      // a duplicate API call — the backend is hit at most once per
      // token per session.
      const intervalId = setInterval(() => {
        const cur = _readOutcome(token);
        if (cur && cur.state !== 'pending') {
          clearInterval(intervalId);
          applyOutcome(cur);
        }
      }, 80);
      // Safety stop after 8s so the user never sees an infinite spinner.
      const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        if (statusRef.current === 'pending') {
          applyOutcome({ state: 'error', detail: 'Time-out bij bevestigen. Vernieuw de pagina om opnieuw te proberen.' });
        }
      }, 8000);
      return () => { cancelled = true; clearInterval(intervalId); clearTimeout(timeoutId); };
    }

    // First mount to see this token in this tab — claim and fetch.
    _writeOutcome(token, { state: 'pending' });
    (async () => {
      try {
        const { data } = await accountApi.confirmEmailChange({ token });
        const outcome = { state: 'ok', newEmail: data?.newEmail || '' };
        _writeOutcome(token, outcome);
        applyOutcome(outcome);
      } catch (err) {
        const detail = err.response?.data?.detail;
        const outcome = {
          state: 'error',
          detail: typeof detail === 'string' ? detail : '',
        };
        _writeOutcome(token, outcome);
        applyOutcome(outcome);
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  return (
    <div
      data-testid="confirm-email-change-page"
      className="min-h-[70vh] flex items-center justify-center px-6"
    >
      <div className="w-full max-w-sm">
        <h1 className="font-serif text-3xl font-light text-center mb-3">E-mailadres bevestigen</h1>

        {status === 'no-token' && (
          <div
            data-testid="confirm-email-no-token"
            className="text-sm bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg"
          >
            Deze link is onvolledig. Vraag een nieuwe wijziging aan via je accountpagina.
          </div>
        )}

        {status === 'pending' && (
          <p
            data-testid="confirm-email-pending"
            className="text-sm text-muted-foreground text-center"
          >
            Bezig met bevestigen…
          </p>
        )}

        {status === 'ok' && (
          <div
            data-testid="confirm-email-success"
            className="space-y-4"
          >
            <div className="text-sm bg-primary/5 border border-primary/20 text-primary px-4 py-3 rounded-lg">
              Je e-mailadres is gewijzigd
              {newEmail ? <> naar <strong>{newEmail}</strong></> : null}
              . Log opnieuw in met je nieuwe adres.
            </div>
            <Link
              to="/login"
              data-testid="confirm-email-go-login"
              className="block text-center text-sm text-primary font-medium hover:underline"
            >
              Direct inloggen
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div
            data-testid="confirm-email-error"
            className="text-sm bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg"
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
